import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { tourDownloadLabel } from '../renderer/tour';

/**
 * PAIGE: the sample tour's Your files. Paige sends each of the five to the job's Download as soon as
 * it is ready; the step says they are exported only once every package is really there
 * (`useTourExport`), and until then says what CapturePoint's says while its export runs. The button
 * saves them as one zip, each a searchable PDF with what was read beside it (document-downloads.ts).
 * (Prod, 2026-10-06: the card said "All five are exported" over five documents that never were, and
 * the button sat on "Getting them ready…" for a minute and a half.)
 */
declare module './paige.global' {
  interface PaigeWorkbenchBridge {
    /** How many of the documents are exported, their packages there to download (workbench-bridge.ts). */
    documentsExported?: (documentIds: readonly string[]) => Promise<number>;
  }
}

const LOOK_EVERY_MS = 2_000;
/** After this long the card stops saying only "Exporting them…" and says Next is there. */
const SLOW_AFTER_MS = 20_000;

/** What the card says once the wait is long: still honest, and the way on. Before that it is CapturePoint's "Exporting them…". */
export const EXPORTING_SLOWLY =
  'Still exporting them. This is taking longer than usual; press **Next** to carry on.';

export interface TourExport {
  /** Every document of the tour's batch has its package: the download will find all of them. */
  ready: boolean;
  /** It has been a while and they are still not all there. */
  slow: boolean;
  /** How many of the tour's documents have their package, and how many there are (0 until known). */
  exported: number;
  total: number;
}

/** The tour's documents: the ids of the job's batch, asked again until there are some. */
async function tourDocuments(jobId: string): Promise<string[]> {
  const batch = await window.capturePointDesktop?.getLatestBatch(jobId);
  return (batch?.documents ?? []).map((document) => document.id);
}

/** How many of the tour's documents are exported right now, of how many; null when the look failed. */
async function countExported(
  jobId: string,
  exported: (documentIds: readonly string[]) => Promise<number>,
): Promise<{ exported: number; total: number } | null> {
  try {
    const ids = await tourDocuments(jobId);
    return { exported: ids.length ? await exported(ids) : 0, total: ids.length };
  } catch {
    return null; // The next look tries again.
  }
}

/** Whether the tour job's documents are all exported, looked at every two seconds while the step shows. */
/**
 * How many of the tour's documents are exported, looked at every two seconds until all are. `documents`, how many the
 * pane holds, starts the looks again when it changes: a Delete and its Restore put back one the count had finished without.
 */
export function useTourExport(
  jobId: string | undefined,
  active: boolean,
  documents = 0,
): TourExport {
  const [state, setState] = useState<TourExport>({
    ready: false,
    slow: false,
    exported: 0,
    total: 0,
  });
  useEffect(() => {
    const exported = window.paigeWorkbench?.documentsExported;
    if (!active || !jobId || !exported) return;
    let live = true;
    let timer = 0;
    const began = Date.now();
    const look = async () => {
      const counted = await countExported(jobId, exported);
      const ready = counted !== null && counted.total > 0 && counted.exported === counted.total;
      if (!live) return;
      // A look that failed keeps the last count: the scene never falls back to nothing exported.
      setState((last) => ({
        ready,
        slow: !ready && Date.now() - began >= SLOW_AFTER_MS,
        exported: counted?.exported ?? last.exported,
        total: counted?.total ?? last.total,
      }));
      if (!ready) timer = window.setTimeout(() => void look(), LOOK_EVERY_MS);
    };
    void look();
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [jobId, active, documents]);
  return state;
}

/** Download all five: the zip is saved, then the tour moves on; a problem stays on the card with Try again. */
export function TourDownload({
  jobId,
  total,
  onSaved,
}: {
  jobId: string;
  /** How many documents the pane holds: a click on the paperclip or scissors changes it. */
  total?: number;
  onSaved: () => void;
}) {
  const [state, setState] = useState<'ready' | 'getting' | 'problem'>('ready');
  const download = async () => {
    const save = window.paigeWorkbench?.downloadDocuments;
    if (!save || state === 'getting') return;
    setState('getting');
    try {
      await save(await tourDocuments(jobId), 'Paige tour documents.zip');
      setState('ready');
      onSaved();
    } catch {
      setState('problem');
    }
  };
  return (
    <button
      type="button"
      className="primary-button small tour-download"
      disabled={state === 'getting'}
      onClick={() => void download()}
    >
      <Download size={14} aria-hidden="true" />
      {state === 'getting'
        ? 'Getting them ready…'
        : state === 'problem'
          ? 'Try again'
          : tourDownloadLabel(total)}
    </button>
  );
}
